import {
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AbsHistory from "../../abstract/History";
import { SortType } from "../../abstract/types";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { useWebSocket, WebSocketEvent } from "../../contexts/WebSocketContext";
import { colors, fontSizes } from "../../theme";
import { formatDate, scoreColor } from "../../utils";
import { GetCycleResponse, GetCycleResponseItem } from "./types";

const History: React.FC = () => {
  const { updateActions, updateBreadcrumbs, translate } = useApp();
  const navigate = useNavigate();
  const { apiService } = useApi();
  const { session } = useAuth();
  const [isSearching, setIsSearching] = useState(false);

  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [data, setData] = useState<GetCycleResponseItem[] | null>(null);
  const { showError, showSuccess } = useError();
  const [disabledId, setDisabledId] = useState<string | null>(null);

  const translations = useMemo(() => translate("sperm"), [translate]);

  const location = useLocation();
  const newId = location.state?.newId;

  const websocket = useWebSocket();

  const handleEvent = useCallback(
    (event: WebSocketEvent) => {
      if (event.name === "cycle_done") {
        showSuccess(translations.history.messages.doneAnalyzingCycle);
        refetchItem(event.data.cycle);
        navigate(location.pathname, {
          replace: true,
          state: { ...location.state, newId: undefined },
        });
      }
    },
    [showSuccess, navigate, location.pathname, location.state, data]
  );

  useEffect(() => {
    websocket.register("sperm", handleEvent);
    return () => {
      websocket.unregister("sperm", handleEvent);
    };
  }, [data]);

  useEffect(() => {
    if (newId && session) {
      setDisabledId(newId);

      const to = setTimeout(() => {
        if (disabledId !== null) {
          setDisabledId(null);
          // refetchItem(newId);
          navigate(location.pathname, {
            replace: true,
            state: { ...location.state, newId: undefined },
          });
          showSuccess(translations.history.messages.doneAnalyzingCycle);
          console.log("Done with timeout");
        }

        return () => clearTimeout(to);
      }, 60000);
    }
  }, [newId, session]);

  const handleOpenItem = (row: GetCycleResponseItem) => {
    if (disabledId === row.id) {
      return;
    }
    navigate(`/sperm/${row.id}`);
  };

  useEffect(() => {
    if (data?.length) {
      updateActions([
        <Button
          key={"create"}
          color="blackButton"
          onClick={() => navigate("/sperm/create")}
        >
          {" "}
          {translations.history.buttons.addNewCycle}{" "}
        </Button>,
      ]);
    }
    updateBreadcrumbs([
      {
        label: translations.breadcrumbs.spermAnalysis,
        onClick: () => {
          navigate("/sperm");
        },
      },
    ]);

    return () => {
      updateActions([]);
      updateBreadcrumbs([]);
    };
  }, [data]);

  const onSearch = (
    sort: SortType<GetCycleResponseItem & { created: string; patient: string; actualOutcome: string }> | null,
    patient: string,
    review: boolean
  ) => {
    // The sort.column may refer to the extended fields, but only pass those that exist in the backend
    // If sort.column is one of the extra fields, you may need to map it to the backend field
    get(sort as SortType<GetCycleResponseItem> | null, patient, review)
      .then((data) => {
        setData(data ?? []);
      })
      .catch((error) => {
        setData([]);
        showError(error);
      });
  };

  const refetchItem = useCallback(
    async (id: string) => {
      if (!data) {
        return;
      }
      try {
        let cData = [...(data ?? [])];
        const cycle = await apiService.get<GetCycleResponse>(`/sperm/${id}`);
        const index = cData.findIndex((c) => c.id === id);
        if (index !== -1) {
          cData[index] = cycle.cycle;
          setData(cData);
          setDisabledId(null);
        }
      } catch (error) {
        showError(translations.history.messages.errorFetchingData);
      }
    },
    [data]
  );

  const get = useCallback(
    async (
      sort: SortType<GetCycleResponseItem> | null = null,
      patient: string = "",
      review: boolean = false,
      pageToken?: string
    ) => {
      console.log("get-Fetching data", sort, patient, review, pageToken);
      const params = new URLSearchParams();
      if (sort) {
        params.append("sort", `${sort.column},${sort.direction}`);
      }
      if (patient) {
        params.append("patient", patient);
      }
      if (review) {
        params.append("review", "1");
      }
      if (pageToken) {
        params.append("page_token", pageToken);
      }

      setIsSearching(true);

      try {
        const response = await apiService.get<{
          next_page_token: string | null;
          cycles: GetCycleResponseItem[];
        }>(`/sperm?${params.toString()}`);

        if (response.next_page_token) {
          setNextPageToken(response.next_page_token);
        }

        console.log(`response`, response);
        return response.cycles;
      } catch (error) {
        console.error(error);
        setData([]);
        showError(translations.history.errors.errorFetchingData);
      } finally {
        setIsSearching(false);
      }
    },
    []
  );


  return (
    <AbsHistory<
      GetCycleResponseItem & { created: string; patient: string; actualOutcome: string }
    >
      isLoadingData={isSearching}
      disabledId={disabledId}
      title={translations.history.title}
      columns={[
        {
          id: "id",
          label: translations.history.columns.cycleId,
          sortable: true,
        },
        {
          id: "patient",
          label: translations.history.columns.patientName,
          sortable: true,
        },
        {
          id: "created",
          label: translations.history.columns.cycleDate,
          sortable: true,
        }
      ]}
      onSearch={onSearch}
      data={
        data
          ? (data.map((cycle) => ({
              ...cycle,
              created: formatDate(new Date(cycle.created)),
            })) as any)
          : null
      }
      onItemClick={handleOpenItem}
    />
  );
};

export default History;
